import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../../constants/property';

const TYPE_OPTIONS = [
  { value: 'stockInPlant', label: 'Stock In — Plant' },
  { value: 'stockInSite', label: 'Stock In — Site' },
  { value: 'stockOutPlant', label: 'Stock Out' },
];

const normalizeGradeEntry = (entry) => {
  if (typeof entry === 'string') {
    const grade = entry.trim();
    return grade ? { grade } : null;
  }
  const grade = String(entry?.grade ?? entry?.name ?? '').trim();
  if (!grade) return null;
  const materialGradeId = Number(entry?.materialGradeId ?? entry?.id ?? 0);
  return materialGradeId > 0 ? { materialGradeId, grade } : { grade };
};

const normalizeUnitEntry = (entry) => {
  if (typeof entry === 'string') {
    const unitName = entry.trim();
    return unitName ? { unitName } : null;
  }
  const unitName = String(entry?.unitName ?? entry?.name ?? '').trim();
  if (!unitName) return null;
  const unitId = Number(entry?.unitId ?? entry?.id ?? 0);
  return unitId > 0 ? { unitId, unitName } : { unitName };
};

const serializeGradeForUpdate = (entry) => {
  const materialGradeId = Number(entry?.materialGradeId ?? 0);
  if (materialGradeId > 0) {
    return { materialGradeId, grade: entry.grade };
  }
  return { grade: entry.grade };
};

const serializeUnitForUpdate = (entry) => {
  const unitId = Number(entry?.unitId ?? 0);
  if (unitId > 0) {
    return { unitId, unitName: entry.unitName };
  }
  return { unitName: entry.unitName };
};

const getEntryKey = (prefix, entry, index) =>
  `${prefix}-${entry.materialGradeId ?? entry.unitId ?? 'new'}-${entry.grade ?? entry.unitName}-${index}`;

const MATERIAL_CATEGORY_OPTIONS = ['Solid', 'Quantity', 'Weight', 'Liquid'];

const DEFAULT_MATERIAL_CATEGORY = MATERIAL_CATEGORY_OPTIONS[0];

/** Registered list: API expects category=`""` (two quote chars) for all categories. */
const REGISTERED_LIST_CATEGORY_ALL = 'all';

const COMMON_UNITS = ['Tonnage', 'CFT'];

const ChipList = ({ items, onRemove }) => (
  <div className="flex flex-wrap gap-2 min-h-[2rem]">
    {items.length === 0 ? (
      <span className="text-sm text-gray-400">None yet — add below</span>
    ) : (
      items.map((item) => (
        <span
          key={item}
          className="inline-flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm"
        >
          {item}
          <button
            type="button"
            onClick={() => onRemove(item)}
            className="text-red-600 hover:text-red-800"
            title="Remove"
          >
            ×
          </button>
        </span>
      ))
    )}
  </div>
);

const EditEntryList = ({ entries, kind, onRemove }) => {
  const labelKey = kind === 'grade' ? 'grade' : 'unitName';
  const idKey = kind === 'grade' ? 'materialGradeId' : 'unitId';

  if (entries.length === 0) {
    return <span className="text-sm text-gray-400">None yet — add below</span>;
  }

  return (
    <div className="space-y-2">
      {entries.map((entry, index) => {
        const entryId = Number(entry[idKey] ?? 0);
        const isExisting = entryId > 0;
        return (
          <div
            key={getEntryKey(kind, entry, index)}
            className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">{entry[labelKey]}</div>
              <div className="text-xs text-gray-500">
                {isExisting ? `${idKey}: ${entryId}` : 'New item (sent without ID)'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemove(entry[labelKey])}
              className="text-sm text-red-600 hover:text-red-800 shrink-0"
            >
              Remove
            </button>
          </div>
        );
      })}
    </div>
  );
};

const AddInline = ({ placeholder, onAdd }) => {
  const [val, setVal] = useState('');
  const add = () => {
    const t = val.trim();
    if (t) {
      onAdd(t);
      setVal('');
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input
        type="text"
        placeholder={placeholder}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        className="rounded-md border border-gray-300 px-3 py-2 text-sm flex-1 min-w-[12rem]"
      />
      <button type="button" className="px-3 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm" onClick={add} style={{ backgroundColor: 'rgb(0, 150, 136)', color: 'white' }}>
        Add
      </button>
    </div>
  );
};

const MaterialType = () => {
  const [materialType, setMaterialType] = useState('');
  const [materialCategory, setMaterialCategory] = useState(DEFAULT_MATERIAL_CATEGORY);
  const [gradeEntries, setGradeEntries] = useState([]);
  const [unitEntries, setUnitEntries] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [scopeType, setScopeType] = useState('stockInPlant');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const materialGrades = useMemo(
    () => gradeEntries.map((g) => g.grade).filter(Boolean),
    [gradeEntries]
  );
  const materialUnits = useMemo(
    () => unitEntries.map((u) => u.unitName).filter(Boolean),
    [unitEntries]
  );

  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [materialsError, setMaterialsError] = useState('');
  const [deletingMaterialKey, setDeletingMaterialKey] = useState('');
  const [registeredListCategory, setRegisteredListCategory] = useState(REGISTERED_LIST_CATEGORY_ALL);

  /**
   * GET /api/material/bytype?type=…&category=%22%22
   * The service returns a consistent list for type only when category is "".
   * Other category query values are not applied reliably server-side, so we filter in the UI.
   */
  const fetchMaterials = useCallback(async () => {
    setMaterialsError('');
    setLoadingMaterials(true);
    try {
      const params = new URLSearchParams({ type: scopeType });
      params.set('category', '""');
      const url = `${API_BASE_URL}/api/material/bytype?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.materials) ? data.materials : []);
      setMaterials(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Material list fetch error:', err);
      setMaterialsError(err.message || 'Failed to load materials.');
      setMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  }, [scopeType]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const addGrade = (g) => {
    const t = g.trim();
    if (!t || materialGrades.includes(t)) return;
    setGradeEntries((prev) => [...prev, { grade: t }]);
    setErrors((e) => ({ ...e, materialGrade: undefined }));
  };

  const removeGrade = (g) => {
    setGradeEntries((prev) => prev.filter((x) => x.grade !== g));
  };

  const addUnit = (u) => {
    const t = u.trim();
    if (!t || materialUnits.includes(t)) return;
    setUnitEntries((prev) => [...prev, { unitName: t }]);
  };

  const removeUnit = (u) => {
    setUnitEntries((prev) => prev.filter((x) => x.unitName !== u));
  };

  const addFormField = (f) => {
    const t = f.trim();
    if (!t || formFields.includes(t)) return;
    setFormFields((prev) => [...prev, t]);
    setErrors((e) => ({ ...e, formFields: undefined }));
  };

  const removeFormField = (f) => {
    setFormFields((prev) => prev.filter((x) => x !== f));
  };

  const validate = () => {
    const next = {};
    if (!materialType.trim()) {
      next.materialType = 'Material type is required.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetForm = ({ preserveScope = false } = {}) => {
    const currentScope = scopeType;
    setMaterialType('');
    setMaterialCategory(DEFAULT_MATERIAL_CATEGORY);
    setGradeEntries([]);
    setUnitEntries([]);
    setFormFields([]);
    setScopeType(preserveScope ? currentScope : 'stockInPlant');
    setIsEditMode(false);
    setEditingRowKey('');
    setErrors({});
  };

  const buildUpdatePayload = () => ({
    materialType: materialType.trim(),
    grades: gradeEntries.map(serializeGradeForUpdate),
    units: unitEntries.map(serializeUnitForUpdate),
    type: scopeType,
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const categoryValue =
      materialCategory.trim() || DEFAULT_MATERIAL_CATEGORY;

    setSaving(true);
    setErrors({});
    try {
      let response;
      if (isEditMode) {
        const updatePayload = buildUpdatePayload();
        response = await fetch(`${API_BASE_URL}/api/material/update`, {
          method: 'PUT',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });
      } else {
        const createPayload = {
          materialType: materialType.trim(),
          materialGrade: [...materialGrades],
          materialUnit: [...materialUnits],
          type: scopeType,
          materialCategory: categoryValue,
          formFields: [...formFields],
        };
        response = await fetch(`${API_BASE_URL}/api/material`, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createPayload),
        });
      }

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }

      alert(isEditMode ? 'Material updated successfully.' : 'Material configuration saved successfully.');
      resetForm();
      await fetchMaterials();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Material API error:', err);
      alert(`Failed to ${isEditMode ? 'update' : 'save'}: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    resetForm({ preserveScope: isEditMode });
  };

  const getMaterialTypeName = (row) =>
    row?.materialType ?? row?.materialtype ?? row?.typeValue ?? '';

  const getGradeEntries = (row) => {
    const raw = Array.isArray(row?.grades)
      ? row.grades
      : (Array.isArray(row?.materialGrade)
        ? row.materialGrade
        : (Array.isArray(row?.materialgrade) ? row.materialgrade : []));
    return raw.map(normalizeGradeEntry).filter(Boolean);
  };

  const getUnitEntries = (row) => {
    const raw = Array.isArray(row?.units)
      ? row.units
      : (Array.isArray(row?.materialUnit)
        ? row.materialUnit
        : (Array.isArray(row?.materialunit) ? row.materialunit : []));
    return raw.map(normalizeUnitEntry).filter(Boolean);
  };

  const getGradeNames = (row) => getGradeEntries(row).map((g) => g.grade);

  const getUnitNames = (row) => getUnitEntries(row).map((u) => u.unitName);

  const getMaterialCategory = (row) =>
    String(row?.materialCategory ?? row?.materialcategory ?? '').trim();

  const displayedMaterials = useMemo(() => {
    if (registeredListCategory === REGISTERED_LIST_CATEGORY_ALL) {
      return materials;
    }
    return materials.filter(
      (row) => getMaterialCategory(row) === registeredListCategory
    );
  }, [materials, registeredListCategory]);

  const getFormFieldNames = (row) => {
    const raw = Array.isArray(row?.formFields)
      ? row.formFields
      : (Array.isArray(row?.formfields) ? row.formfields : []);
    return raw
      .map((f) => (typeof f === 'string' ? f : (f?.name ?? f?.field ?? '')))
      .map((x) => String(x).trim())
      .filter(Boolean);
  };

  const onEditMaterial = (row) => {
    const matType = getMaterialTypeName(row);
    if (!matType) {
      alert('Unable to edit: material type is missing.');
      return;
    }
    const category = getMaterialCategory(row) || DEFAULT_MATERIAL_CATEGORY;
    const scope = String(row?.type ?? row?.scopeType ?? scopeType).trim() || scopeType;
    const rowKey = String(row?.materialTypeId ?? row?.materialId ?? row?.id ?? `${matType}-${scope}`);

    setMaterialType(matType);
    setMaterialCategory(
      MATERIAL_CATEGORY_OPTIONS.includes(category) ? category : DEFAULT_MATERIAL_CATEGORY
    );
    setGradeEntries(getGradeEntries(row));
    setUnitEntries(getUnitEntries(row));
    setFormFields(getFormFieldNames(row));
    setScopeType(scope);
    setIsEditMode(true);
    setEditingRowKey(rowKey);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeleteMaterial = async (row) => {
    const materialTypeName = getMaterialTypeName(row);
    if (!materialTypeName) {
      alert('Unable to delete: material type is missing.');
      return;
    }
    const ok = window.confirm(`Delete material "${materialTypeName}"?`);
    if (!ok) return;

    const rowId = row?.materialTypeId ?? row?.materialId ?? row?.id;
    const key = String(rowId ?? materialTypeName);
    setDeletingMaterialKey(key);
    try {
      const scope = String(row?.type ?? row?.scopeType ?? scopeType).trim();
      const params = new URLSearchParams({
        materialType: materialTypeName,
        type: scope,
      });
      const response = await fetch(`${API_BASE_URL}/api/material/delete?${params.toString()}`, {
        method: 'DELETE',
        mode: 'cors',
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      await fetchMaterials();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Material delete error:', err);
      alert(`Failed to delete material: ${err.message || 'Unknown error'}`);
    } finally {
      setDeletingMaterialKey('');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Material type maintenance</h2>
        {/* <p className="text-sm text-gray-600 mt-1">
          Create material definitions sent to{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">POST /api/material</code> with{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">materialType</code>,{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">materialGrade</code>,{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">materialUnit</code>, and{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">type</code>.
        </p> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <form
          onSubmit={onSubmit}
          className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-6"
        >
          {/* {isEditMode && (
            <div className="rounded-md border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900 space-y-2">
              <p>
                Editing <span className="font-medium">{materialType}</span>. Existing grades/units keep their IDs; newly added items are sent without IDs.
              </p>
              <pre className="overflow-x-auto rounded bg-white/80 p-3 text-xs text-gray-800 border border-teal-100">
                {JSON.stringify(buildUpdatePayload(), null, 2)}
              </pre>
            </div>
          )} */}

          {/* Scope / type + category */}
          <div className="space-y-2">
            <div className="flex flex-col gap-3">
              <div className="w-full max-w-xs">
                <label htmlFor="scopeType" className="block text-sm font-medium text-gray-700">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="scopeType"
                  value={scopeType}
                  onChange={(e) => setScopeType(e.target.value)}
                  disabled={isEditMode}
                  className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:text-gray-600"
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {!isEditMode && (
              <fieldset className="w-full">
                <legend className="block text-sm font-medium text-gray-700">Material category</legend>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  {MATERIAL_CATEGORY_OPTIONS.map((option) => (
                    <label key={option} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="materialCategory"
                        value={option}
                        checked={materialCategory === option}
                        onChange={(e) => setMaterialCategory(e.target.value)}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </fieldset>
              )}
            </div>
          </div>

          {/* Material type */}
          <div className="space-y-2">
            <label htmlFor="materialType" className="block text-sm font-medium text-gray-700">
              Material type <span className="text-red-500">*</span>
            </label>
            <input
              id="materialType"
              type="text"
              value={materialType}
              onChange={(e) => {
                setMaterialType(e.target.value);
                setErrors((prev) => ({ ...prev, materialType: undefined }));
              }}
              readOnly={isEditMode}
              placeholder="e.g. Aggregate"
              autoComplete="off"
              className={`block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                errors.materialType ? 'border-red-500' : 'border-gray-300'
              } ${isEditMode ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            />
            {errors.materialType && (
              <p className="text-sm text-red-600" role="alert">
                {errors.materialType}
              </p>
            )}
          </div>

          {/* Grades → materialGrade[] */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="text-sm font-medium text-gray-800">Material grades</div>
            {isEditMode ? (
              <EditEntryList entries={gradeEntries} kind="grade" onRemove={removeGrade} />
            ) : (
              <ChipList items={materialGrades} onRemove={removeGrade} />
            )}
            <AddInline placeholder="e.g. Chips, 6MM, 12MM" onAdd={addGrade} />
            {errors.materialGrade && <p className="text-sm text-red-600">{errors.materialGrade}</p>}
          </div>

          {/* Units → materialUnit[] */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="text-sm font-medium text-gray-800">Material units</div>
            <div className="flex flex-wrap gap-2">
              {COMMON_UNITS.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => addUnit(u)}
                  disabled={materialUnits.includes(u)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + {u}
                </button>
              ))}
            </div>
            {isEditMode ? (
              <EditEntryList entries={unitEntries} kind="unit" onRemove={removeUnit} />
            ) : (
              <ChipList items={materialUnits} onRemove={removeUnit} />
            )}
            <AddInline placeholder="Add custom unit label" onAdd={addUnit} />
          </div>

          {/* Dynamic form field keys → formFields[] */}
          {!isEditMode && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="text-sm font-medium text-gray-800">Form fields</div>
           
            <ChipList items={formFields} onRemove={removeFormField} />
            <AddInline placeholder="e.g. grade, quantity" onAdd={addFormField} />
            {errors.formFields && <p className="text-sm text-red-600">{errors.formFields}</p>}
          </div>
          )}

          {/* Preview */}
          {/* <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-xs font-mono whitespace-pre-wrap break-words">
            {JSON.stringify(
              {
                materialType: materialType.trim() || '(required)',
                materialGrade: materialGrades.length ? materialGrades : [],
                materialUnit: materialUnits.length ? materialUnits : [],
                type: scopeType,
                materialCategory: materialCategory.trim() || DEFAULT_MATERIAL_CATEGORY,
                formFields: formFields.length ? formFields : [],
              },
              null,
              2
            )}
          </div> */}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
                saving ? 'bg-teal-600/70 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              style={{ backgroundColor: '#009688' }}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Material Type' : 'Add Material Type'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              {isEditMode ? 'Cancel edit' : 'Clear form'}
            </button>
          </div>
        </form>

        <div className="lg:col-span-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2 min-w-0 flex-1">
              <h3 className="font-medium text-gray-900">Registered materials</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {TYPE_OPTIONS.find((o) => o.value === scopeType)?.label ?? scopeType}
                {registeredListCategory === REGISTERED_LIST_CATEGORY_ALL
                  ? ' · all categories'
                  : ` · ${registeredListCategory}`}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="registeredListCategory" className="text-sm text-gray-700 whitespace-nowrap">
                  Category filter
                </label>
                <select
                  id="registeredListCategory"
                  value={registeredListCategory}
                  onChange={(e) => setRegisteredListCategory(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary max-w-full"
                >
                  <option value={REGISTERED_LIST_CATEGORY_ALL}>All categories</option>
                  {MATERIAL_CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchMaterials}
              disabled={loadingMaterials}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60 shrink-0"
            >
              {loadingMaterials ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {materialsError && (
            <p className="text-sm text-red-600" role="alert">
              {materialsError}
            </p>
          )}

          {loadingMaterials && materials.length === 0 && !materialsError ? (
            <p className="text-sm text-gray-500">Loading materials…</p>
          ) : materials.length === 0 ? (
            <p className="text-sm text-gray-500">No materials yet. Use the form on the left.</p>
          ) : displayedMaterials.length === 0 ? (
            <p className="text-sm text-gray-500">
              No materials in this category for the selected type. Try &quot;All categories&quot; or another filter.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Material type</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Category</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Grades</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Units</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Form fields</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Scope</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700 w-36">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {displayedMaterials.map((row) => {
                    const grades = getGradeNames(row);
                    const units = getUnitNames(row);
                    const matType = getMaterialTypeName(row);
                    const category = getMaterialCategory(row);
                    const fields = getFormFieldNames(row);
                    const rowScope = row.type ?? row.scopeType ?? scopeType;
                    const scopeLabel =
                      TYPE_OPTIONS.find((o) => o.value === rowScope)?.label ?? rowScope;
                    const rowKey = String(row?.materialTypeId ?? row?.materialId ?? row?.id ?? `${matType}-${rowScope}`);
                    return (
                      <tr key={rowKey}>
                        <td className="px-4 py-2 text-gray-900">{matType || '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{category || '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{grades.length ? grades.join(', ') : '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{units.length ? units.join(', ') : '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{fields.length ? fields.join(', ') : '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{scopeLabel || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => onEditMaterial(row)}
                              disabled={deletingMaterialKey === rowKey || (isEditMode && editingRowKey === rowKey)}
                              className="text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                            >
                              {isEditMode && editingRowKey === rowKey ? 'Editing' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteMaterial(row)}
                              disabled={deletingMaterialKey === rowKey}
                              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              {deletingMaterialKey === rowKey ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialType;
